/* Shared CEF surface kit: preview-mode ribbon + rich demo data.
   When a surface is opened without a player session token (i.e. from a browser,
   not inside the game), it renders with this living demo world instead of an
   error wall — owners and devs can see exactly what players will see. */

export function PreviewRibbon({ what }) {
  return (
    <div data-testid="cef-preview-ribbon" className="absolute top-0 inset-x-0 z-50 flex items-center justify-center gap-3 bg-amber-500/95 text-black px-4 py-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
      <p className="font-mono text-[9px] uppercase tracking-[0.25em] font-bold">
        Preview mode — {what} · in-game this connects to your live character via the server bridge
      </p>
    </div>
  );
}

/* ACB / legal fiction disclosure — mounted on every NCDB-records surface (CAD, court).
   All records in these systems are fictional in-game data, never real-world history. */
export function NcdbDisclaimer() {
  return (
    <div data-testid="ncdb-disclaimer" className="pointer-events-none fixed bottom-0 inset-x-0 z-50 flex items-center justify-center px-3 py-1 bg-black/70 border-t border-white/10">
      <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 text-center">
        All NCDB records are fictional in-game data for entertainment purposes only — they do not represent real-world criminal history
      </p>
    </div>
  );
}

/* Simulated-gambling disclosure — R18+ classification marker on all casino surfaces. */
export function GamblingNotice() {
  return (
    <div data-testid="gambling-notice" className="pointer-events-none fixed bottom-0 inset-x-0 z-50 flex items-center justify-center px-3 py-1 bg-black/70 border-t border-[#d4af3733]">
      <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 text-center">
        Simulated gambling · 18+ only · chips are fictional in-game currency with no real-world monetary value and cannot be cashed out or refunded
      </p>
    </div>
  );
}


/* In-game detection: UE5's CEF plugin exposes window.ue; the bridge can also force it
   with ?ingame=1. When true, website-bound links must NOT navigate — a player who
   clicks through to the web hub inside the game overlay would be trapped there with
   no way back to the menu. App.js renders an escape bar on non-CEF routes instead. */
export const IS_INGAME = typeof window !== "undefined" && (typeof window.ue !== "undefined" || /[?&]ingame=1\b/.test(window.location.search));

const now = Date.now();
const iso = (minAgo) => new Date(now - minAgo * 60000).toISOString();

export const DEMO = {
  phone: {
    playercode: 37049, name: "Demo Citizen", bank: 12450, cash: 860, isblocked: false, synced_at: iso(0),
    pending_transfers: [{ id: "pd1", to_playercode: 22110, amount: 500 }],
    transactions: [
      { txid: "t1099", description: "Paycheck — Downtown Cab Co.", amount: 1250, timestamp: iso(40) },
      { txid: "t1098", description: "Transfer to #22110", amount: -500, timestamp: iso(180) },
      { txid: "t1097", description: "Burger Shot", amount: -34, timestamp: iso(300) },
      { txid: "t1096", description: "ATM withdrawal", amount: -200, timestamp: iso(560) },
      { txid: "t1095", description: "Mechanic invoice #4482", amount: -1750, timestamp: iso(1440) },
      { txid: "t1094", description: "Paycheck — Downtown Cab Co.", amount: 1250, timestamp: iso(1600) },
    ],
  },

  cadAlerts: [
    { id: "da1", ref: "INC-2614", category: "multi", status: "open", priority: 1, services: ["police", "ems"],
      summary: "Shots fired, multiple callers — two males down at the lumber yard, suspects fled in a black Sultan RS towards the freeway.",
      location: { label: "North Hollow Lumber Yard", x: -552, y: 5320 }, caller: "Anonymous", created_at: iso(2) },
    { id: "da2", ref: "INC-2613", category: "police", status: "dispatched", priority: 2, services: ["police"],
      summary: "Armed robbery in progress at 24/7, clerk reports one suspect with a sawn-off, no shots yet.",
      location: { label: "24/7 — Innocence Blvd", x: 24, y: -1346 }, caller: "Store Clerk", created_at: iso(6) },
    { id: "da3", ref: "INC-2612", category: "ems", status: "dispatched", priority: 3, services: ["ems"],
      summary: "Traffic collision, single vehicle into a pole — driver unconscious, airbag deployed.",
      location: { label: "Pierpoint Fwy NB", x: -1580, y: -520 }, caller: "Passerby", created_at: iso(11) },
    { id: "da4", ref: "INC-2611", category: "fire", status: "closed", priority: 3, services: ["fire"],
      summary: "Dumpster fire behind Maze Bank Arena — extinguished, no spread.",
      location: { label: "Maze Bank Arena", x: -250, y: -1980 }, caller: "Security", created_at: iso(28) },
    { id: "da5", ref: "MIS-0042", category: "police", status: "open", priority: 2, services: ["police"], via: "mission",
      summary: "10-55 — Possible DUI driver: Caller reports a sedan swerving across both lanes — nearly clipped two parked cars.",
      location: { label: "Route 68 & Innocence Blvd", x: 215, y: -2100 }, caller: "DISPATCH AUTO", created_at: iso(3),
      mission: { template: "dui_driver", title: "Possible DUI driver", objective: "Locate, stop safely, field-sobriety test, impound if over.", reward: 340, expires_at: new Date(Date.now() + 17 * 60000).toISOString(), spawned: true, paid: false } },
  ],

  cadUnits: [
    { callsign: "1-L-12", name: "S. Vega", agency: "police", status: "10-8", beat: "Crestwood" },
    { callsign: "1-L-07", name: "D. King", agency: "police", status: "10-6", beat: "INC-2613" },
    { callsign: "2-A-03", name: "M. Ortiz", agency: "ems", status: "10-8", beat: "Pillbox" },
    { callsign: "2-A-11", name: "R. Hayes", agency: "ems", status: "10-7", beat: "INC-2612" },
    { callsign: "3-E-01", name: "T. Williams", agency: "fire", status: "10-8", beat: "Station 3" },
    { callsign: "1-A-01", name: "A. O'Connor", agency: "police", status: "10-8", beat: "Air support" },
  ],

  court: {
    viewer: { playercode: 0, is_judge: false },
    court_style: "corrupt",
    cases: [
      { id: "demo-case-1", docket: "CRT-26-0142", status: "awaiting_hearing", plea: "",
        defendant: { name: "Marcus Webb", playercode: 55231 },
        charges: [{ title: "Reckless Evading", severity: "felony", fine: 4500, jail_seconds: 900 },
                  { title: "Failure to Obey a Traffic Control Device", severity: "misdemeanor", fine: 800, jail_seconds: 0 }] },
      { id: "demo-case-2", docket: "CRT-26-0139", status: "verdict", plea: "no_contest",
        defendant: { name: "Lena Cruz", playercode: 61042 },
        charges: [{ title: "Possession of an Unlicensed Firearm", severity: "misdemeanor", fine: 2200, jail_seconds: 0 }] },
    ],
  },

  courtDesk: {
    warrants: [
      { id: "dw1", name: "Dwight Kingsley", playercode: 77003, reason: "Armed robbery of the 24/7 — armed and dangerous", serious: true, funds_locked: true, issued_by: "Sgt. Okafor", at: iso(900), signed_by: "" },
      { id: "dw2", name: "Ajax O'Connor", playercode: 11884, reason: "Chop-shop ring — felony GTA x3", serious: false, funds_locked: false, issued_by: "Ofc. S. Vega", at: iso(1500), signed_by: "R. Castellanos" },
    ],
    subpoenas: [
      { id: "ds1", target_name: "Marcus Webb", target_playercode: 55231, reason: "Coordinating the lumber yard job by text — need message log.", requester_name: "Sgt. Okafor", status: "pending", at: iso(600) },
      { id: "ds2", target_name: "Lena Cruz", target_playercode: 61042, reason: "Firearm supplier contacts.", requester_name: "Ofc. D. King", status: "approved", decided_by: "Judge R. Castellanos", at: iso(2400) },
    ],
  },

  courtCase: {
    "demo-case-1": {
      id: "demo-case-1", docket: "CRT-26-0142", status: "awaiting_hearing", plea: "", arrest_ref: "INC-2598",
      defendant: { name: "Marcus Webb", playercode: 55231 }, arresting_officer: "Ofc. S. Vega (1-L-12)",
      charges: [{ title: "Reckless Evading", severity: "felony", fine: 4500, jail_seconds: 900 },
                { title: "Failure to Obey a Traffic Control Device", severity: "misdemeanor", fine: 800, jail_seconds: 0 }],
      evidence_summary: "Dashcam shows the defendant's vehicle failing to stop across three intersections. Recovered vehicle matched plates to the defendant.",
      interviews: [{ npc_name: "Gas Station Attendant", clues_revealed: ["Saw the black Sultan refuel 10 minutes before the pursuit", "Driver wore a yellow racing jacket"] }],
    },
    "demo-case-2": {
      id: "demo-case-2", docket: "CRT-26-0139", status: "verdict", plea: "no_contest",
      defendant: { name: "Lena Cruz", playercode: 61042 }, arresting_officer: "Ofc. D. King (1-L-07)",
      charges: [{ title: "Possession of an Unlicensed Firearm", severity: "misdemeanor", fine: 2200, jail_seconds: 0 }],
      evidence_summary: "Firearm recovered during a lawful traffic stop; serial filed.",
      interviews: [],
      verdict: { outcome: "guilty", fine: 2200, jail_seconds: 0, judge: "Hon. R. Castellanos", at: iso(300), notes: "Plea accepted. Fine payable within 7 days." },
    },
  },

  admin: {
    overview: {
      server: { id: "SRV-OFFICIAL-1", name: "Official Server 1", mode: "Hardcore RP", region: "OCE" },
      operator: { playercode: 37049 }, live: true,
      world: { players: 23, max_players: 64, weather: "rain", game_time: "23:41", map: "Bornwood",
               on_duty: { police: 6, ems: 2, fire: 1 }, hostname: "New Citizen Official AU #1",
               roster: [
                 { playercode: 37049, name: "Demo Citizen", job: "civ", ping: 32, steamid: "76561198012345678", ip: "203.0.113.10" },
                 { playercode: 88001, name: "Sarah Vega", job: "police", ping: 41, steamid: "76561198023456789", ip: "203.0.113.21" },
                 { playercode: 88002, name: "Desmond King", job: "police", ping: 55, steamid: "76561198034567890", ip: "198.51.100.14" },
                 { playercode: 88003, name: "Mia Ortiz", job: "ems", ping: 28, steamid: "76561198045678901", ip: "198.51.100.77" },
                 { playercode: 88004, name: "Ray Hayes", job: "ems", ping: 61, steamid: "76561198056789012", ip: "192.0.2.55" },
                 { playercode: 88005, name: "Tommy Williams", job: "fire", ping: 47, steamid: "76561198067890123", ip: "192.0.2.90" },
                 { playercode: 88006, name: "Ajax O'Connor", job: "police", ping: 38, steamid: "76561198078901234", ip: "203.0.113.99" },
                 { playercode: 55231, name: "Marcus Webb", job: "civ", ping: 92, steamid: "76561198089012345", ip: "198.51.100.130", network_flags: 2, flag_categories: ["griefing", "ban evasion"] },
               ] },
      economy: { starting_cash: 5000, paycheck_amount: 300, tax_income: 10, tax_sales: 8, tax_vehicle: 5 },
      counts: { open_cad: 2, pending_whitelist: 1, active_bans: 1, pending_commands: 0 },
    },
    commands: [
      { id: "c1", type: "warn", target_name: "Marcus Webb", target_playercode: 55231, reason: "VDM at Legion — clip saved", by: "pc:37049", status: "done", created_at: iso(35), acked_at: iso(34) },
      { id: "c2", type: "announce", reason: "Restart in 10 minutes — finish your scenes", by: "pc:37049", status: "done", created_at: iso(90), acked_at: iso(89) },
      { id: "c3", type: "set_weather", params: { weather: "rain" }, by: "pc:37049", status: "done", created_at: iso(140), acked_at: iso(139) },
    ],
    bans: [{ playercode: 99120, name: "xX_Griefer_Xx", reason: "Mass RDM — appeal on Discord", by: "pc:37049", created_at: iso(2900), active: true }],
    whitelist: [{ id: "w1", callsign: "J. Marlowe", age_bracket: "18-24", experience: "3 years FiveM RP, mostly BPD. Sgt on two servers.", why: "Want serious RP with actual court and economy systems.", status: "pending", created_at: iso(200) }],
    network_reports: [
      { id: "nr1", server_id: "SRV-OFFICIAL-1", server_name: "Official Server 1", reporter_pc: 37049, target_playercode: 55231, target_name: "Marcus Webb", target_steamid: "76561198089012345", category: "griefing", reason: "Mass VDM at Founders Square three nights running — kicked twice, comes back and does it again.", evidence_url: "https://clips.specpixel.com/watch/vdm-legion-03", evidence_text: "", status: "pending", reviewed_by: "", review_note: "", created_at: iso(160), reviewed_at: null },
      { id: "nr2", server_id: "SRV-OFFICIAL-1", server_name: "Official Server 1", reporter_pc: 37049, target_playercode: 99120, target_name: "xX_Griefer_Xx", target_steamid: "", category: "ban evasion", reason: "Banned last week for mass RDM, back tonight on a fresh Steam account with the same voice and same antics.", evidence_url: "", evidence_text: "Same mic, same voice, admitted it in OOC before disconnecting. IP matches the banned account's last connection.", status: "approved", reviewed_by: "Specpixel Staff", review_note: "Confirmed — hardware + IP match", created_at: iso(2900), reviewed_at: iso(2800) },
    ],
  },

  politics: {
    server: "Official Server 1",
    government: { office: "Mayor", name: "R. O'Connor", playercode: 22110, since: iso(43200) },
    election: { id: "demo-el", office: "Mayor", status: "voting", created_at: iso(2880), voting_at: iso(1400), total_votes: 92 },
    candidates: [
      { id: "c1", name: "Ada King", slogan: "Safer streets, funded EMS, and a transparent city budget — published weekly.", votes: 41, playercode: 88001 },
      { id: "c2", name: "Ray Dalton", slogan: "Lower sales tax for business owners. Cut the red tape at City Hall.", votes: 33, playercode: 88009 },
      { id: "c3", name: "Mia Torres", slogan: "Night-market permits, live music districts, and nightlife revival.", votes: 18, playercode: 88014 },
    ],
    past: [{ id: "p1", office: "Mayor", winner: { name: "R. O'Connor" }, total_votes: 87, closed_at: iso(43200) }],
    you: { playercode: 37049, name: "Demo Citizen", candidate_id: "", voted_for: "" },
  },
};
